// module imports
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

// file imports
import axiosInstance from '@/lib/axios';
import { IEvent } from '@/types';

interface GetEventArgs {
  params?: Record<string, string | number>;
}

const addEvent = createAsyncThunk('event/addEvent', async (eventData: IEvent, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post('/events', eventData);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.error || 'Adding event failed');
  }
});

const getEvents = createAsyncThunk('event/getEvents', async ({ params = {} }: GetEventArgs, { rejectWithValue }) => {
  try {
    const query = new URLSearchParams(params as Record<string, string>).toString();
    const response = await axiosInstance.get(`/events${query ? `?${query}` : ''}`);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.error || 'Retrieving events failed');
  }
});

const getEvent = createAsyncThunk('event/getEvent', async (eventSlug: string, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get(`/events/${eventSlug}`);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.error || 'Retrieving single event failed');
  }
});

const updateEvent = createAsyncThunk('event/updateEvent', async (params: { eventSlug: string; updatedData: any }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.patch(`/events/${params.eventSlug}`, params.updatedData);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.error || 'Updating event failed');
  }
});

const deleteEvent = createAsyncThunk('event/deleteEvent', async (eventSlug: string, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.delete(`/events/${eventSlug}`);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.error || 'Deleting event failed');
  }
});

const eventSlice = createSlice({
  name: 'event',
  initialState: {
    data: null as any,
    loading: false,
    error: null as string | null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // addEvent
      .addCase(addEvent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addEvent.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(addEvent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // getEvents
      .addCase(getEvents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getEvents.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(getEvents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // getEvent
      .addCase(getEvent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getEvent.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(getEvent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // updateEvent
      .addCase(updateEvent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateEvent.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(updateEvent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // deleteEvent
      .addCase(deleteEvent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteEvent.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(deleteEvent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError } = eventSlice.actions;
export { addEvent, deleteEvent, getEvent, getEvents, updateEvent };
export default eventSlice.reducer;
